import { ArrowDropDown } from "@mui/icons-material";
import {
  Box,
  Button,
  Grid2,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  Drawer,
  Stack,
  IconButton,
  Popover,
  Divider,
  MenuList,
  MenuItem,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import useStyles from "./styles";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ModalDateTime from "./pickerDateTimeCustom";

const optionsTimesRange = [
  { value: 5, unit: "minutos", unitEn: "minutes" },
  { value: 15, unit: "minutos", unitEn: "minutes" },
  { value: 30, unit: "minutos", unitEn: "minutes" },
  { value: 1, unit: "hora", unitEn: "hour" },
  { value: 3, unit: "horas", unitEn: "hours" },
  { value: 6, unit: "horas", unitEn: "hours" },
  { value: 12, unit: "horas", unitEn: "hours" },
  { value: 24, unit: "horas", unitEn: "hours" },
  { value: 2, unit: "dias", unitEn: "days" },
  { value: 7, unit: "dias", unitEn: "days" },
  { value: 1, unit: "mês", unitEn: "month" },
  { value: 3, unit: "meses", unitEn: "months" },
  { value: 6, unit: "meses", unitEn: "months" },
  { value: 1, unit: "ano", unitEn: "year" },
];

const TimeFilter = forwardRef((props, ref) => {
  const {
    onTimeChange = () => {},
    timeZone = "America/Sao_Paulo",
    loading = false,
    disabled = false,
    initialDate = null,
    finalDate = null,
    relitiveInterval = false,
    anchorElDate,
    setAnchorElDate,
  } = props;

  const [filterDateInicial, setFilterDateInicial] = useState(initialDate);
  const [filterDateFinal, setFilterDateFinal] = useState(finalDate);
  const [openDrawer, setOpenDrawer] = useState(false);

  const theme = useTheme();
  const styles = useStyles(theme);
  const upMD = useMediaQuery(theme.breakpoints.up("md"));

  useImperativeHandle(ref, () => ({
    getTimeFilter() {
      return { filterDateInicial, filterDateFinal };
    },
    setTimeFilter(initial, final) {
      if (initial) setFilterDateInicial(initial);
      if (final) setFilterDateFinal(final);
    },
  }));

  useEffect(() => {
    if (initialDate) {
      setFilterDateInicial(initialDate);
    }
  }, [initialDate]);

  useEffect(() => {
    if (finalDate) {
      setFilterDateFinal(finalDate);
    }
  }, [finalDate]);

  useEffect(() => {
    if (!timeZone) return;
    setFilterDateInicial((prev) => prev?.tz(timeZone) || prev);
    setFilterDateFinal((prev) => prev?.tz(timeZone) || prev);
  }, [timeZone]);

  const handleClick = (event) => {
    setAnchorElDate(event.currentTarget);
  };

  const handleTimeChange = (newInitial, newFinal) => {
    setFilterDateInicial(newInitial);
    setFilterDateFinal(newFinal);
    onTimeChange({
      initial: newInitial,
      final: newFinal,
    });
  };

  const formatDateRange = () => {
    if (!filterDateInicial && !filterDateFinal) {
      return "Filtrar por Período";
    }

    if (filterDateInicial && !filterDateFinal) {
      return `A partir de ${dayjs(filterDateInicial).format("DD/MM/YYYY HH:mm")}`;
    }

    if (!filterDateInicial && filterDateFinal) {
      return `Até ${dayjs(filterDateFinal).format("DD/MM/YYYY HH:mm")}`;
    }

    return `${dayjs(filterDateInicial).format("DD/MM/YYYY HH:mm")} até ${dayjs(
      filterDateFinal,
    ).format("DD/MM/YYYY HH:mm")}`;
  };

  const TimeFilterContent = () => (
    <Paper
      elevation={0}
      sx={{
        ...styles.paperBrick,
        ...((loading || disabled) && styles.disable),
        cursor: loading || disabled ? "not-allowed" : "pointer",
        border: anchorElDate
          ? `1px solid ${theme.palette.primary.main}`
          : "none",
      }}
      onClick={(e) => !loading && !disabled && handleClick(e)}
    >
      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <CalendarMonthIcon
          sx={{ ...styles.size, color: theme.palette.color.bgIcon }}
        />
        <Typography sx={styles.text}>{`${formatDateRange()}`}</Typography>
      </Stack>
      <ArrowDropDown
        sx={
          anchorElDate
            ? { ...styles.size, transform: "rotate(180deg)" }
            : { ...styles.size }
        }
      />
    </Paper>
  );

  return (
    <>
      {/* Versão Desktop */}
      <Box sx={{ width: "100%" }}>
        <TimeFilterContent />
      </Box>

      <ModalDateTime
        key={"modalDateTime"}
        anchorEl={anchorElDate}
        setAnchorEl={setAnchorElDate}
        dateInitial={filterDateInicial}
        dateFinal={filterDateFinal}
        setDateFinal={(date) => handleTimeChange(filterDateInicial, date)}
        setDateInitial={(date) => handleTimeChange(date, filterDateFinal)}
        clear={() => handleTimeChange(null, null)}
        relitiveInterval={relitiveInterval}
      />
    </>
  );
});

export default TimeFilter;
